import { TrackTopTimesStatTableColumns } from "components/StatsTable/ColumnConfigs/Track/TopTimes/TopTimes";
import { StatTable } from "components/StatsTable/StatTable";
import { isFinish } from "model/Match/DriverTime";
import { Match, getMatchDriverTimes } from "model/Match/Match";
import { TopTimesTrackStats } from "stats/Track/TopTimes/TopTimesTrackStats";
import { getRosterIdFromDriverId } from "model/Roster/Roster";
import {
  anyStringContains,
  arrayCompareWithShallowEquals,
  nonUndefined,
} from "Utility";
import {
  selectCompletedMatches,
  selectTournamentIdFromMatchId,
} from "selectors/Match";
import {
  selectRosterOrgDetails,
  selectRosterScope,
  selectTournamentDriverName,
} from "selectors/Roster";
import { useAppSelector } from "app/hooks";
import { shallowEqual } from "react-redux";

interface Props {
  trackId: string;
  matchIds: string[];
}

export const TrackTopTimesStats = ({ trackId, matchIds }: Props) => {
  const completeMatches = useAppSelector(
    (state) => selectCompletedMatches(state, matchIds),
    shallowEqual
  );

  const driverTimes = getValidDriverTimes(completeMatches, trackId);
  driverTimes.sort((a, b) => {
    if (a.finishTime === b.finishTime) {
      return a.match.startTimeMs - b.match.startTimeMs;
    }

    return a.finishTime - b.finishTime;
  });

  const topTimes: TopTimesTrackStats[] = useAppSelector((state) => {
    return nonUndefined(
      driverTimes.map((x, i) => {
        const rank = i + 1;
        const timeMs = x.finishTime;
        let lowestRank = rank;
        for (let j = i - 1; j >= 0; j--) {
          if (driverTimes[j].finishTime !== timeMs) {
            break;
          }

          lowestRank = j + 1;
        }

        const tournamentId = selectTournamentIdFromMatchId(state, x.match.id);
        const rosterScope = !!tournamentId
          ? selectRosterScope(state, tournamentId)
          : undefined;
        const rosterId = !!rosterScope
          ? getRosterIdFromDriverId(
              x.driverId,
              x.match.teams.rosters,
              rosterScope
            )
          : undefined;

        if (!rosterId || !tournamentId) {
          return undefined;
        }

        const { id: orgId, name: orgName } =
          selectRosterOrgDetails(state, rosterId, tournamentId) || {};

        const driverName =
          selectTournamentDriverName(state, x.driverId, tournamentId) ?? "???";

        return {
          rank,
          rankText: lowestRank.toString(),
          orgId,
          orgName,
          driverName,
          timeMs,
          dateMs: x.match.startTimeMs,
        };
      })
    );
  }, arrayCompareWithShallowEquals);

  if (topTimes.length === 0) {
    return <></>;
  }

  return (
    <StatTable
      thinner={true}
      stats={topTimes}
      keyGetter={(t) => `${t.rank}-${t.driverName}`}
      columns={TrackTopTimesStatTableColumns}
      paginationAmount={10}
      searchFunction={(arg, searchText) =>
        anyStringContains(searchText, arg.driverName, arg.orgName)
      }
      searchPlaceholder={"Filter: Team or Driver. Comma for multiple"}
    />
  );
};

const getValidDriverTimes = (matches: Match[], trackId: string) => {
  return matches.flatMap((m) =>
    getMatchDriverTimes(m, false, trackId)
      .filter(isFinish)
      .map((d) => ({
        match: m,
        driverId: d.driverId,
        finishTime: d.finishTimeMs!,
      }))
  );
};
